<template>
    <div 
        class="conversations--message-container overflow-hidden" 
        :class="{
            'card-disabled': userLoggedIn.category == 'seller' && getConversationId == '',
            'mt-0 border-radius-top-left-0 border-radius-bottom-left-0 border-0' : $route.name === 'NegotiationsSeller',
            'card' : !$breakpoint.smAndDown,
        }"
    >
        <div class="overlay bottom"></div>
        <div class="text-center py-3 border-bottom"  v-if="!isNegotiationMessaging">
            {{counterpartName}} - <button type="button" class="btn btn-link" @click="goToMyBuyersOrSellers()">{{accountName}}</button>
        </div>
        <div 
            class="conversations--message-content pb-4" id="chat-room-container"
            :class="{'d-flex justify-content-center align-items-center' : userLoggedIn.category == 'seller' && getConversationId == '' && isNegotiationMessaging}"
        >


            <!-- Display the following message only on Negotiations if seller has not placed a counter -->
            <span v-if="userLoggedIn.category == 'seller' && getConversationId == '' && isNegotiationMessaging" 
                class="disabled-message">
                <div>Chat will become available once a counter offer is submitted</div>
            </span>

            <div v-else 
                class="conversations--message my-2"
                :class="[$breakpoint.smAndDown ? 'mx-4' : 'mr-3 ml-4']"
                v-for="(message, key) in messagesList" :key="key">
                <!-- Day separator -->
                <div class="conversations--day-tripper" v-if="key == 0 || key !== 0 &&
                    moment(message.sent).format('DD-MM-YYYY') !== moment(messagesList[key - 1].sent).format('DD-MM-YYYY')">
                    <div class="text">
                        <span class="bg-white px-3" v-if="moment(message.sent).isSame(moment(), 'day')">Today</span>
                        <span class="bg-white px-3" v-else>{{message.sent | timestampformat}}</span>
                    </div>
                </div>
                
                <!-- Message bubble -->
                <div class="conversations-bubble" :class="message.user_id == userLoggedIn.userId ? 'user text-right' : 'recipient text-left'">
                    <!-- Username on negotiation -->
                    <strong v-if="isNegotiationMessaging && message.user_id !== userLoggedIn.userId" class="username">
                        {{participants.find(participant => participant.id == message.user_id).firstName}} {{participants.find(participant => participant.id == message.user_id).lastName}}
                    </strong>
                    <!-- Message -->
                    {{message.message}}
                    <!-- Timestamp -->
                    <small class="timestamp">{{message.sent | timeformat}}</small>
                </div>
            </div>
        </div>

        <!-- Display the following to notify user that chat will be closed 10 mins after negotiation is completed -->
        <div v-if="isNegotiationMessaging && !negotiationChatDisabled && negotiation.status_id == 'COMPLETED' && 
            ((userLoggedIn.category == 'buyer' && 
            (negotiation.counterOffers[activeTabIndex - 1].status_id == 'COMPLETED' || negotiation.accepting_user.account.id == negotiation.counterOffers[activeTabIndex - 1].user.account.id))
            || (userLoggedIn.category == 'seller' && hasPlacedCounter &&
            (wasMyCounterAccepted[activeTabIndex - 1] || negotiation.accepting_user.account.id == userLoggedIn.id)))" 
            class="closing-negotiation-message">
            Your negotiation was successful.<br/>This chat will remain open until {{Date.parse(negotiation.updated) + 10 * 60 * 1000 | timeformat}}
        </div>

        <div class="conversations--message-input mx-4">
            <form v-if="!negotiationChatDisabled" v-on:submit.prevent="onSubmit">
                <textarea class="message-input pl-0" rows="2" type="text" placeholder="Write a message here..." v-model="inputData" 
                    @keydown.enter.exact.prevent="onSubmit" :disabled="userLoggedIn.category == 'seller' && getConversationId == ''">
                </textarea>
                <button class="btn btn-secondary btn-send-message" type="submit" 
                    :disabled="(userLoggedIn.category == 'seller' && getConversationId == '') || inputData.trim().length < 1">
                    <i class="fas fa-arrow-right"></i>
                </button>
            </form>
            <div v-else>
                <strong>This negotiation is now closed.<br/>Please use Chat</strong>
            </div>
        </div>
    </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import breakpoint from '@/mixins/index';

import { eNavbarTitles } from '@/util/enums/navbar';

export default {
    mixins: [breakpoint],
    data:function(){
        return {
            inputData: '',
            closingNegotiationTimeout: '',
            negotiationExpiryTimeout: '',
            counterExpiryTimeout: '',
            negotiationChatDisabled: false,
            accountName: '',
            counterpartName: ''
        }
    },
    created(){
        this.getAccountName();
    },
    updated(){
        this.getAccountName();
    },
    methods:{
        ...mapActions('conversation',['sendMessage', 'deselectConversation', 'getGenericConversationsList']),
        ...mapActions('notifications', ['sendNotification', 'updateNotification']),
        ...mapGetters('conversation',['getParticipants']),
        ...mapGetters('login', ['getUserId', 'getAccountType']),
        onSubmit: function (){
            /* Prevent sending an empty message or only whitespace */
            if (this.inputData.trim().length < 1){
                return
            }
            
            let payload = {
                conversationId: this.getConversationId,
                message: this.inputData,
            }

            /* If this is negotiation messaging, then attach negotiation and counter offer ids */
            if (this.isNegotiationMessaging){
                payload.negotiationId = this.userLoggedIn.category == 'buyer' ? 
                    this.negotiationSelected._id : this.fuelGroupSelected.negotiationList[this.activeTabIndex - 1]._id
                payload.counterOfferId = this.userLoggedIn.category == 'buyer' ?
                    this.negotiationSelected.counterOffers[this.activeTabIndex - 1]._id : this.placedCounter._id
            }

            this.sendMessage(payload).then(() => {
                /* Create payload for Notification */
                let notificationPayload = {
                    group_id: this.isNegotiationMessaging ? payload.negotiationId : this.getConversationId,
                    content: this.inputData,
                    participants: this.participants, // an array of participants
                    type_id: this.isNegotiationMessaging ? 'NEGOTIATION' : 'MESSAGE', // dynamic type
                }
                if (this.isNegotiationMessaging){
                    notificationPayload.meta = {
                        counterOfferId: payload.counterOfferId
                    }
                }
                this.sendNotification(notificationPayload); // call Notification Action
                this.inputData = '' // clears input once message & notification actions are finished
                
                /* If this is on OLF Chat page, then fetch updated conversations to update message preview */
                if (!this.isNegotiationMessaging){
                    this.getGenericConversationsList()
                }

                /* Remove red dot notifications for this conversation/negotiation */
                let currentRelevantNotification = this.notificationList.find(notification => {
                    if (notification.type_id === 'MESSAGE' && payload.conversationId === notification.group_id && notification.viewed === null){
                        return notification
                    }
                    else if (notification.type_id === 'NEGOTIATION'){
                        if (this.userLoggedIn.category === 'buyer' && payload.negotiationId === notification.group_id && notification.meta && payload.counterOfferId === notification.meta.counterOfferId && notification.viewed === null)
                            return notification
                        else if (this.userLoggedIn.category === 'seller' && payload.negotiationId === notification.group_id && notification.viewed === null)
                            return notification
                    }
                })

                if(currentRelevantNotification){
                    this.updateNotification(currentRelevantNotification.group_id);
                }
            })
        },
        moment: function (date) {
            const moment = require('moment')
            return moment(date);
        },
        disableExpiredNegotiationChat: function(){
            const counterExpiry = Date.parse(this.userLoggedIn.category == 'buyer' ? 
                this.negotiation.counterOffers[this.activeTabIndex - 1].expires : this.placedCounter.expires) - new Date()

            /* Set timeout to log counter offer's status as expired */
            this.counterExpiryTimeout = setTimeout(() => {
                this.negotiationChatDisabled = true
            }, counterExpiry)
        },
        getAccountName(){
            const counterpartUser = this.getParticipants().find(counterpartUserId => counterpartUserId.id !== this.getUserId())
            if (counterpartUser) {
                this.counterpartName = `${counterpartUser.firstName} ${counterpartUser.lastName}`
                this.getCounterpartAccountName(counterpartUser.id)
            }
            
        },
        getCounterpartAccountName: async function(counterpartUserId){
            const response = await this.axios.get('/api/getUserDetails', {params: {
                userId: counterpartUserId
            }})

            if (response.data.success){
                this.accountName = response.data.user.account.name
            }
        },
        goToMyBuyersOrSellers(){
            if(this.getAccountType() === 'seller'){
                this.$router.push('/MyCustomers');
                return;
            }
            this.$router.push('/MySuppliers')
        },
    },
    computed:{
        ...mapState('conversation', ['participants']),
        ...mapState('login', ['userLoggedIn']),
        ...mapState('negotiation', ['activeTabIndex', 'negotiationSelected', 'fuelGroupSelected', 'placedCounter']),
        ...mapState('notifications', ['notificationList']),
        ...mapGetters('conversation', ['getConversationId', 'messagesList']),
        ...mapGetters('negotiation', ['wasMyCounterAccepted', 'hasPlacedCounter']),
        isNegotiationMessaging: {
            get: function(){
                const { negotiations } = eNavbarTitles;
                const currentRouteTitle = this.$route.meta.title;
                if (currentRouteTitle === negotiations){
                    return true
                }
                else {
                    return false
                }
            }
        },
        negotiation: {
            get: function(){
                if (this.isNegotiationMessaging){
                    return this.userLoggedIn.category == 'buyer' ? 
                        this.negotiationSelected : this.fuelGroupSelected.negotiationList[this.activeTabIndex - 1]
                }
            }
        },
        scrollToLastMessage: function(){
            if (this.getConversationId && this.messagesList.length){
                setTimeout(() => {
                    const chatRoomContainer = document.getElementById('chat-room-container');
                    chatRoomContainer.scrollTop = chatRoomContainer.scrollHeight;
                }, 100)
            }
        },
        disableNegotiationChat: function(){
            clearTimeout(this.closingNegotiationTimeout)
            
            if (this.isNegotiationMessaging && this.getConversationId && this.negotiation){
                /* Disable negotiation chat 10 mins after negotiation has been completed */
                if (this.negotiation.status_id == 'COMPLETED'){
                    if ((this.userLoggedIn.category == 'buyer' && 
                        (this.negotiation.counterOffers[this.activeTabIndex - 1].status_id == 'COMPLETED' || this.negotiation.accepting_user.account.id == this.negotiation.counterOffers[this.activeTabIndex - 1].user.account.id)) || 
                        (this.userLoggedIn.category == 'seller' && this.hasPlacedCounter &&
                        (this.wasMyCounterAccepted[this.activeTabIndex - 1] || this.negotiation.accepting_user.account.id == this.userLoggedIn.id))
                    ){
                        clearTimeout(this.negotiationCountdownFunction)
                        clearTimeout(this.counterExpiryTimeout)
                        const timeRemaining = Date.parse(this.negotiation.updated) + 10 * 60 * 1000
                        this.closingNegotiationTimeout = setTimeout(() => {
                            this.negotiationChatDisabled = true
                        }, timeRemaining - new Date())
                    }
                    else if (this.userLoggedIn.category == 'buyer' && this.negotiation.counterOffers[this.activeTabIndex - 1].status_id !== 'COMPLETED'){
                        setTimeout(() => {
                            this.negotiationChatDisabled = true
                        }, 100)
                    }
                }
                /* Disable negotiaiton chat once negotiaiton has been withdrawn */
                else if (this.negotiation.status_id == 'WITHDRAWN'){
                    this.negotiationChatDisabled = true
                }
            }
        },
        countNegotiationExpiry: function(){
            if (this.isNegotiationMessaging && this.negotiation){
                this.negotiationChatDisabled = false

                if (this.negotiationCountdownFunction){
                    clearInterval(this.negotiationCountdownFunction)
                }

                /* Set timeout to log negotiation as expired */
                if (this.negotiation.status_id == 'ACTIVE'){
                    this.negotiationTimeRemaining = new Date(this.negotiation.expires) - new Date()
                    this.negotiationCountdownFunction = setTimeout(() => {
                        /* Once negotiation has expired, check counter offer's status */
                        this.disableExpiredNegotiationChat()
                    }, this.negotiationTimeRemaining)
                }
                else if (this.negotiation.status_id == 'WITHDRAWN'){
                    this.negotiationChatDisabled = true
                }
                /* Display message if negotiation is closed and seller has not submitted any counter */
                else if (!this.getConversationId){
                    this.disableExpiredNegotiationChat()
                }
            }
        },
    },
    watch: {
        scrollToLastMessage(){},
        disableNegotiationChat(){},
        countNegotiationExpiry(){}
    },
    beforeDestroy(){
        this.deselectConversation()
        clearTimeout(this.closingNegotiationTimeout)
        clearTimeout(this.negotiationExpiryTimeout)
        clearTimeout(this.counterExpiryTimeout)
    }
}
</script>

<style lang="scss" scoped>
@import "@/assets/scss/components/_conversations.scss";
@import "@/assets/scss/components/_conversations-bubble.scss";
</style>
